import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Check, AlertCircle, Crown, Sparkles, RefreshCw, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  'Unlimited Coach360 AI conversations',
  'Personalized meal & workout plans',
  'Full biometric tracking & insights',
  '21-day habit stacking program',
  'Weekly wellness reports',
  'Priority support',
];

const plans = [
  { id: 'monthly', name: 'Monthly', price: '$20', period: '/month', popular: false },
  { id: 'yearly', name: 'Yearly', price: '$180', period: '/year', popular: true, savings: 'Save $60' },
];

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [checkingPayment, setCheckingPayment] = useState(false);

  const fetchBillingStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/billing/status`);
      setBillingStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch billing:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/billing/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        updateUser({ subscription_status: 'active' });
        await fetchBillingStatus();
        toast.success('Payment successful! Welcome to Premium! 🎉');
        // Clear URL params
        window.history.replaceState({}, '', '/billing');
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
      toast.error('Error checking payment status');
    }
  }, [fetchBillingStatus, updateUser]);

  useEffect(() => {
    // Handle Stripe redirect
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const isMock = searchParams.get('mock');

    if (sessionId && success === 'true') {
      setCheckingPayment(true);
      
      if (isMock === 'true') {
        // For mock sessions, simulate instant success
        setTimeout(async () => {
          await pollPaymentStatus(sessionId);
        }, 1000);
      } else {
        pollPaymentStatus(sessionId);
      }
    } else if (canceled === 'true') {
      toast.info('Checkout canceled');
      window.history.replaceState({}, '', '/billing');
    }
    
    fetchBillingStatus();
  }, [searchParams, fetchBillingStatus, pollPaymentStatus]);

  const handleSubscribe = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/billing/start-checkout-session`, {
        plan_id: selectedPlan,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to start checkout');
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/billing/cancel`);
      updateUser({ subscription_status: 'canceled' });
      setBillingStatus(prev => ({ ...prev, subscription_status: 'canceled' }));
      setShowCancelModal(false);
      toast.success('Subscription canceled');
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const isTrialActive = billingStatus?.subscription_status === 'trial';
  const isPremium = billingStatus?.subscription_status === 'active';
  const isExpired = billingStatus?.subscription_status === 'expired';
  const trialEndDate = billingStatus?.trial_end_date 
    ? new Date(billingStatus.trial_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  // Calculate days remaining in trial
  const daysRemaining = billingStatus?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(billingStatus.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <h1 className="font-heading font-bold text-lg">Billing & Plan</h1>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Payment Processing Overlay */}
        {checkingPayment && (
          <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <Card className="glass-card p-8 text-center max-w-sm mx-4">
              <RefreshCw size={40} className="text-primary animate-spin mx-auto mb-4" />
              <h2 className="font-heading font-bold text-xl mb-2">Processing Payment</h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we confirm your payment...
              </p>
            </Card>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw size={24} className="text-primary animate-spin" />
          </div>
        ) : (
          <>
            {/* Current Plan Card */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className={`glass-card p-6 ${isPremium ? 'border-[#8BB89F]/50' : isTrialActive ? 'border-primary/50' : 'border-[#F8B64C]/50'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {isPremium ? (
                        <Crown size={20} className="text-[#8BB89F]" />
                      ) : (
                        <Sparkles size={20} className={isExpired ? 'text-[#F8B64C]' : 'text-primary'} />
                      )}
                      <h2 className="font-heading font-bold text-lg">
                        {isPremium ? 'Premium' : isTrialActive ? 'Free Trial' : 'Expired'}
                      </h2>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {isPremium 
                        ? 'Full access to all features' 
                        : isTrialActive 
                          ? `${daysRemaining} days remaining`
                          : 'Upgrade to continue using HealthLink360'
                      }
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isPremium 
                      ? 'bg-[#8BB89F]/20 text-[#8BB89F]' 
                      : isTrialActive
                        ? 'bg-primary/20 text-primary'
                        : 'bg-[#F8B64C]/20 text-[#F8B64C]'
                  }`}>
                    {isPremium ? 'Active' : isTrialActive ? 'Trial' : 'Expired'}
                  </div>
                </div>

                {isTrialActive && daysRemaining !== null && (
                  <div className="p-3 rounded-xl bg-primary/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-primary" />
                      <p className="text-primary font-medium text-sm">3-month free trial</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Trial ends {trialEndDate}. Subscribe to continue after trial.
                    </p>
                    {/* Progress bar */}
                    <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.max(0, (90 - daysRemaining) / 90 * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {isExpired && (
                  <div className="p-3 rounded-xl bg-[#F8B64C]/10">
                    <p className="text-[#F8B64C] font-medium text-sm">Your trial has ended</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Subscribe to continue accessing your health data and insights.
                    </p>
                  </div>
                )}
              </Card>
            </motion.section>

            {/* Plan Selection */}
            {!isPremium && (
              <section>
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                  Choose a Plan
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`p-4 rounded-2xl border-2 text-left transition-all ${
                        selectedPlan === plan.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/30'
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      {plan.popular && (
                        <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full mb-2 inline-block">
                          Popular
                        </span>
                      )}
                      <p className="font-bold text-lg">{plan.price}</p>
                      <p className="text-xs text-muted-foreground">{plan.period}</p>
                      {plan.savings && (
                        <p className="text-xs text-[#8BB89F] font-medium mt-1">{plan.savings}</p>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Premium Features */}
            <section>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                {isPremium ? 'Your Features' : 'Premium Features'}
              </h2>
              <Card className="glass-card p-4">
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="p-1 rounded-full bg-[#8BB89F]/20">
                        <Check size={14} className="text-[#8BB89F]" />
                      </div>
                      <span className="text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </section>

            {/* Action Buttons */}
            {!isPremium && (
              <Button
                onClick={handleSubscribe}
                disabled={actionLoading}
                className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                data-testid="subscribe-btn"
              >
                {actionLoading ? (
                  <>
                    <RefreshCw size={16} className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  `Subscribe ${plans.find(p => p.id === selectedPlan)?.price}${plans.find(p => p.id === selectedPlan)?.period}`
                )}
              </Button>
            )}

            {isPremium && (
              <Button
                variant="outline"
                onClick={() => setShowCancelModal(true)}
                className="w-full rounded-full text-destructive border-destructive/30"
                data-testid="cancel-subscription-btn"
              >
                Cancel Subscription
              </Button>
            )}

            {/* Payment Info */}
            {isPremium && (
              <section>
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                  Payment Method
                </h2>
                <Card className="glass-card p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <CreditCard size={20} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">•••• •••• •••• 4242</p>
                      <p className="text-xs text-muted-foreground">Expires 12/26</p>
                    </div>
                  </div>
                </Card>
              </section>
            )}
          </>
        )}
      </main>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm"
          >
            <Card className="glass-card p-6 text-center">
              <AlertCircle size={32} className="text-[#F8B64C] mx-auto mb-4" />
              <h2 className="font-heading font-bold text-xl mb-2">Cancel Subscription?</h2>
              <p className="text-sm text-muted-foreground mb-6">
                You&apos;ll lose access to premium features at the end of your billing period.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 rounded-full"
                >
                  Keep Plan
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="flex-1 bg-destructive hover:bg-destructive/90 rounded-full"
                  data-testid="confirm-cancel-btn"
                >
                  {actionLoading ? 'Canceling...' : 'Cancel'}
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
